// components/ui/dialog.tsx

"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

// Primitives
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-1100 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out " +
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogTitleBoundaryProps = {
  fallback: React.ReactNode;
  children: React.ReactNode;
};

type DialogTitleBoundaryState = {
  hasError: boolean;
};

class DialogTitleBoundary extends React.Component<DialogTitleBoundaryProps, DialogTitleBoundaryState> {
  constructor(props: DialogTitleBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): DialogTitleBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: DialogTitleBoundaryProps) {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogTitleBoundary
    fallback={
      <h2 className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
    }
  >
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  </DialogTitleBoundary>
));
DialogTitle.displayName = "DialogTitle";

// ← UPDATED: render a <div> instead of a <p> to allow block children
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Description asChild ref={ref} {...props}>
    <div className={cn("text-sm text-muted-foreground", className)}>
      {children}
    </div>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = "DialogDescription";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string;
  /**
   * When true, avoid transform-based centering/animations.
   * This is useful for drag-and-drop libraries that misbehave under transformed ancestors.
   */
  noTransform?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, overlayClassName, noTransform = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    {noTransform ? (
      (() => {
        const raw = typeof className === "string" ? className : ""
        const zMatches = raw.match(/(?:^|\s)z-(?:\[[^\]]+\]|\d+)(?=\s|$)/g) || []
        const zClass = zMatches.length > 0 ? zMatches[zMatches.length - 1].trim() : ""
        const wrapperZNum = zClass ? parseInt((zClass.match(/\d+/) || ["0"])[0], 10) : 10110
        const safeWrapperZ = wrapperZNum > 10100 ? zClass : "z-[10110]"
        return (
          <div
            className={cn(
              "fixed inset-0 flex items-center justify-center p-4 min-h-0",
              safeWrapperZ
            )}
          >
            <DialogPrimitive.Content
              ref={ref}
              className={cn(
                "relative w-full max-w-lg max-h-[calc(100dvh-2rem)] my-auto shrink-0 p-0 border-0 bg-transparent shadow-lg duration-200 " +
                  "data-[state=open]:animate-in data-[state=closed]:animate-out " +
                  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                className
              )}
              {...props}
            >
              {children}
            </DialogPrimitive.Content>
          </div>
        )
      })()
    ) : (
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "z-1110 grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 " +
            "data-[state=open]:animate-in data-[state=closed]:animate-out " +
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 " +
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] " +
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
          // Keep DialogContent reliably centered even if consumers pass `relative`, `top-0`, etc.
          "fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    )}
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
/**
 * Convenience re-export, trimmed on 2026-08-01 to the two tokens still reached
 * through this path (`ui/command.tsx`, `feed/marketplace-vertical-snap-feed.tsx`
 * — the two frozen non-`DialogCard` shells). Everything else imports from
 * `@/lib/dialog-styles` directly; new dialogs need none of it, because
 * `components/ui/dialog-card.tsx` applies the tokens itself.
 */
export { DIALOG_CARD_OVERLAY_CLASS, DIALOG_CARD_CONTENT_BASE } from "@/lib/dialog-styles";
